import { useCallback, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { useTaxonomy } from "../../../../context/TaxonomyProvider";
import type { TaxonomyNodeView } from "../../../../lib/taxonomyView";

interface FieldPickerProps {
  /** Currently selected field URNs. */
  value: string[];
  onChange: (value: string[]) => void;
  /** When provided, restricts available options to this subset of URNs. */
  allowedUrns?: string[];
  label?: string;
  disabled?: boolean;
  size?: "small" | "medium";
}

function getDomainLabel(field: TaxonomyNodeView): string {
  return field.domain?.enName ?? "Other";
}

/** Controlled multi-select field picker, options grouped by domain and sorted alphabetically. */
export function FieldPicker({
  value,
  onChange,
  allowedUrns,
  label = "Fields",
  disabled = false,
  size = "medium",
}: FieldPickerProps) {
  const taxonomy = useTaxonomy();
  const options = useMemo(() => {
    const fields = taxonomy.fields;
    // Domain first, so each group is contiguous (the grouping needs it), then name; both A to Z.
    return (allowedUrns ? fields.filter((n) => allowedUrns.includes(n.urn)) : [...fields]).sort(
      (a, b) =>
        getDomainLabel(a).localeCompare(getDomainLabel(b)) || a.enName.localeCompare(b.enName)
    );
  }, [taxonomy, allowedUrns]);

  const selectedNodes = options.filter((n) => value.includes(n.urn));

  return (
    <Autocomplete
      multiple
      options={options}
      groupBy={getDomainLabel}
      getOptionLabel={(n) => n.enName}
      value={selectedNodes}
      onChange={(_, nodes) => onChange(nodes.map((n) => n.urn))}
      isOptionEqualToValue={(a, b) => a.urn === b.urn}
      disabled={disabled}
      size={size}
      renderTags={(nodes, getTagProps) =>
        nodes.map((node, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={node.enName} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
